import { ChevronRight, Clock } from "@assets";
import { Row } from "@components/Flex";
import { Title4 } from "@components/Typography";
import { Flow, Routes, SipFrequency, SipStatus } from "@ctypes";
import {
  useErrorBanner,
  useGoalFlow,
  useGoalsContext,
  useNavigateToStep,
  useTheme,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { formatCurrency } from "@utils";
import { useStyles } from "./styles";
import { GoalDepositProps } from "./types";

export const GoalDeposit = ({ style }: GoalDepositProps) => {
  const { theme } = useTheme();
  const styles = useStyles();

  const navigate = useNavigateToStep();
  const { showErrorBanner } = useErrorBanner();

  const { hasBankAccount } = useUser();

  const { goal, goalId, beneficiaryId, goalName, goalType, sipId, annualDeposit } =
    useGoalsContext();

  const { resetGoalProps } = useGoalFlow();

  const shouldDisplayCreateRecurringDeposit = !sipId || !hasBankAccount;
  const shouldDisplayUnPauseRecurringDeposit = goal?.sips?.some(
    (g) => g?.status === SipStatus.Paused,
  );

  const hasAnnualDeposits = goal?.sips?.some(
    (g) => g?.recurringDepositSchedule === SipFrequency.Annually,
  );

  const hasMonthlyDeposits = goal?.sips?.some(
    (g) => g?.recurringDepositSchedule === SipFrequency.Monthly,
  );

  const hasDailyDeposits = goal?.sips?.some(
    (g) => g?.recurringDepositSchedule === SipFrequency.Daily,
  );

  const depositAmount = (() => {
    if (hasAnnualDeposits) return annualDeposit;

    if (hasMonthlyDeposits) return annualDeposit / 12;

    if (hasDailyDeposits) return annualDeposit / 365;

    return annualDeposit / 52;
  })();

  const titleLabel = (() => {
    if (shouldDisplayCreateRecurringDeposit) {
      return i18n.t(Translations.GoalBreakdownRouteCreateRecurringDeposit);
    }
    if (shouldDisplayUnPauseRecurringDeposit) {
      return i18n.t(Translations.GoalBreakdownRouteUnpauseRecurringDeposit);
    }

    const amount = formatCurrency(depositAmount);

    if (hasAnnualDeposits) {
      return i18n.t(Translations.GoalDepositAnuallyLabel, {
        amount,
      });
    }
    if (hasMonthlyDeposits) {
      return i18n.t(Translations.GoalDepositMonthlyLabel, {
        amount,
      });
    }
    if (hasDailyDeposits) {
      return i18n.t(Translations.GoalDepositDailyLabel, {
        amount,
      });
    }
    return i18n.t(Translations.GoalDepositWeeklyLabel, {
      amount,
    });
  })();

  const handleSubmit = () => {
    if (!hasBankAccount) {
      navigate(Routes.BankLinking, { state: { flow: Flow.B2B } });
    } else if (goalId && beneficiaryId) {
      resetGoalProps({
        flow: Flow.EditGoal,
        goalId,
        beneficiaryId,
        goalName,
        goalType,
        ...(sipId && { sipId }),
      });

      navigate(Routes.GoalDeposit);
    } else {
      showErrorBanner();
    }
  };

  return (
    <div style={{ ...styles.container, ...style }} onClick={handleSubmit}>
      <Row>
        <Clock fill={theme.goal.deposits.text} style={styles.icon} />
        <Title4 style={styles.text}>{titleLabel}</Title4>
        <ChevronRight fill={theme.goal.deposits.text} />
      </Row>
    </div>
  );
};
